import { get, set } from 'lodash-unified'
import type { Entries } from 'type-fest'
import type { Arrayable } from '.'

export const keysOf = <T>(arr: T) => Object.keys(arr as any) as Array<keyof T>
export const entriesOf = <T>(arr: T) => Object.entries(arr as any) as Entries<T>
export { hasOwn } from '@vue/shared'

export const getProp = <T = any>(
  obj: Record<string, any>,
  path: Arrayable<string>,
  defaultValue?: any
): { value: T } => {
  return {
    get value() {
      return get(obj, path, defaultValue)
    },
    set value(val: any) {
      set(obj, path, val)
    },
  }
}

/**
 * 分离对象中的 on 和 props，并重命名 onXxx 为 xxx
 * @param obj
 * @returns { on: {}, props: {} }
 */
export function separateOnAndProps<T extends Record<string, any>>(obj: T) {
  const on: Record<string, any> = {}
  const props: Record<string, any> = {}

  // 正则表达式：匹配以 "on" 开头，且后面紧跟大写字母的属性
  const eventPattern = /^on[A-Z][a-zA-Z]*$/

  // 遍历对象，分离符合 onXxx 格式的事件和其他属性
  Object.entries(obj).forEach(([key, value]) => {
    if (eventPattern.test(key)) {
      // 将 onXxx 转换为 xxx 格式，并转为小写
      const eventName = key.replace(/^on([A-Z])/, (_, letter) =>
        letter.toLowerCase()
      )
      on[eventName] = value
    } else {
      // 其他属性放入 props 对象中
      props[key] = value
    }
  })

  return { on, props }
}

export type Writeable<T> = { -readonly [P in keyof T]: T[P] }

export function pick<T, U extends keyof T>(
  obj: T,
  keys: ReadonlyArray<U>,
  ignoreUndefined?: boolean
) {
  return keys.reduce((ret, key) => {
    if (!ignoreUndefined || obj[key] !== undefined) {
      ret[key] = obj[key]
    }
    return ret
  }, {} as Writeable<Pick<T, U>>)
}

export const flat = <T>(arr: Array<T | T[]>) =>
  arr.reduce<T[]>((acc, val) => acc.concat(val), [])

export const isSameValue = (newValue: unknown, oldValue: unknown) =>
  JSON.stringify(newValue) === JSON.stringify(oldValue)
